import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { authAPI } from '../api';
import {
  colors,
  fonts,
  radius,
  shadow,
  buttonPrimary,
  focusRing,
} from '../theme';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(28, 27, 23, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 1000;
`;

const Modal = styled.div`
  position: relative;
  background: ${colors.card};
  border: 1px solid ${colors.ruleStrong};
  border-radius: ${radius.md};
  box-shadow: ${shadow.modal};
  padding: 28px;
  width: 380px;
  max-width: 100%;
`;

const Title = styled.h2`
  font-family: ${fonts.display};
  font-size: 22px;
  margin: 0 0 4px;
`;

const Blurb = styled.p`
  margin: 0 0 20px;
  font-size: 13px;
  color: ${colors.inkFaint};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Input = styled.input`
  padding: 9px 11px;
  border: 1px solid ${colors.ruleStrong};
  border-radius: ${radius.sm};
  background: ${colors.card};
  color: ${colors.ink};
  font-size: 15px;

  &::placeholder {
    color: ${colors.inkFaint};
  }

  &:focus {
    outline: none;
    border-color: ${colors.accent};
  }
`;

const SubmitButton = styled.button`
  ${buttonPrimary}
  ${focusRing}
  margin-top: 4px;
  padding: 11px 18px;
`;

const SwitchButton = styled.button`
  background: none;
  border: none;
  margin-top: 14px;
  padding: 0;
  width: 100%;
  color: ${colors.accent};
  font-size: 13px;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
  ${focusRing}

  &:hover {
    color: ${colors.accentHover};
  }
`;

const ErrorMessage = styled.div`
  padding: 8px 11px;
  border: 1px solid ${colors.danger};
  border-radius: ${radius.sm};
  background: ${colors.dangerWash};
  color: ${colors.danger};
  font-size: 13px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 12px;
  background: none;
  border: none;
  font-size: 22px;
  line-height: 1;
  color: ${colors.inkFaint};
  cursor: pointer;
  border-radius: ${radius.sm};
  ${focusRing}

  &:hover {
    color: ${colors.ink};
  }
`;

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAuthSuccess: (token: string) => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, onAuthSuccess }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) emailRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!isLogin) {
        await authAPI.register(email, password);
      }
      const response = await authAPI.login(email, password);
      localStorage.setItem('token', response.access_token);
      onAuthSuccess(response.access_token);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'That didn’t work. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
role="presentation"
    >
      <Modal role="dialog" aria-modal="true" aria-label={isLogin ? 'Sign in' : 'Create an account'}>
        <CloseButton onClick={onClose} aria-label="Close">
          &times;
        </CloseButton>
        <Title>{isLogin ? 'Sign in' : 'Create an account'}</Title>
        <Blurb>Keep your streak and your place on the leaderboard.</Blurb>

        <Form onSubmit={handleSubmit}>
          <Input
            ref={emailRef}
            type="email"
            placeholder="Email"
            autoComplete="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />
          <Input
            type="password"
            placeholder="Password"
            autoComplete={isLogin ? 'current-password' : 'new-password'}
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
          />
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <SubmitButton type="submit" disabled={loading}>
            {loading ? 'One moment…' : isLogin ? 'Sign in' : 'Create account'}
          </SubmitButton>
        </Form>

        <SwitchButton
          type="button"
          onClick={() => {
            setIsLogin(!isLogin);
            setError('');
          }}
        >
          {isLogin ? 'Need an account? Create one' : 'Already have an account? Sign in'}
        </SwitchButton>
      </Modal>
    </Overlay>
  );
};

export default AuthModal;
